<template>
  <div class="oferta-form-fields">
    <!-- Selector de tipo de criterio -->
    <v-card outlined class="mb-4 criterios-card" elevation="2">
      <v-card-title class="py-3 bg-gradient-info">
        <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
        <span class="white--text">Criterios de Aplicación</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <v-alert type="info" dense text class="mb-4">
          <strong>Ofertas Dinámicas:</strong> Selecciona criterios (marcas, modelos, tamaños) para que los nuevos productos que crees también entren automáticamente en esta oferta.
        </v-alert>

        <v-tabs v-model="tipoCriterioActivo" color="primary" grow class="mb-4">
          <v-tab>
            <v-icon left>mdi-package-variant</v-icon>
            Productos
          </v-tab>
          <v-tab>
            <v-icon left>mdi-tag-multiple</v-icon>
            Marcas
          </v-tab>
          <v-tab>
            <v-icon left>mdi-shape</v-icon>
            Modelos
          </v-tab>
          <v-tab>
            <v-icon left>mdi-ruler</v-icon>
            Tamaños
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tipoCriterioActivo">
          <!-- Tab: Productos Específicos -->
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.criterios.productos"
                  :items="productosItems"
                  item-text="text"
                  item-value="value"
                  label="Seleccionar productos específicos"
                  outlined
                  dense
                  multiple
                  chips
                  deletable-chips
                  :error="hasInputError('criterios.productos')"
                  :error-messages="getInputError('criterios.productos')"
                  prepend-inner-icon="mdi-package-variant"
                  :loading="loadingProductos"
                  :search-input.sync="searchProductos"
                  @update:search-input="searchProductosHandler"
                  hint="Selecciona productos específicos para esta oferta"
                  persistent-hint
                  class="rounded-lg pt-1"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="index < 3"
                      small
                      close
                      @click:close="removeItem('productos', item)"
                      color="primary"
                      text-color="white"
                      class="ma-1"
                    >
                      <v-icon small left>mdi-tag</v-icon>
                      {{ getProductoLabel(item) }}
                    </v-chip>
                    <span
                      v-if="index === 3"
                      class="grey--text text-caption ml-2"
                    >
                      (+{{ form.criterios.productos.length - 3 }} más)
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar color="primary" class="my-2">
                      <v-icon dark small>mdi-package</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ getProductoLabel(item) }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Tab: Marcas -->
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.criterios.marcas"
                  :items="marcasItems"
                  item-text="nombre"
                  item-value="id"
                  label="Seleccionar marcas"
                  outlined
                  dense
                  multiple
                  chips
                  deletable-chips
                  :error="hasInputError('criterios.marcas')"
                  :error-messages="getInputError('criterios.marcas')"
                  prepend-inner-icon="mdi-tag-multiple"
                  :loading="loadingMarcas"
                  hint="Todos los productos de estas marcas entrarán en la oferta"
                  persistent-hint
                  class="rounded-lg pt-1"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="index < 5"
                      small
                      close
                      @click:close="removeItem('marcas', item.id)"
                      color="success"
                      text-color="white"
                      class="ma-1"
                    >
                      <v-icon small left>mdi-tag</v-icon>
                      {{ item.nombre }}
                    </v-chip>
                    <span
                      v-if="index === 5"
                      class="grey--text text-caption ml-2"
                    >
                      (+{{ form.criterios.marcas.length - 5 }} más)
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Tab: Modelos -->
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.criterios.modelos"
                  :items="modelosItems"
                  item-text="nombre"
                  item-value="id"
                  label="Seleccionar modelos"
                  outlined
                  dense
                  multiple
                  chips
                  deletable-chips
                  :error="hasInputError('criterios.modelos')"
                  :error-messages="getInputError('criterios.modelos')"
                  prepend-inner-icon="mdi-shape"
                  :loading="loadingModelos"
                  hint="Todos los productos de estos modelos entrarán en la oferta"
                  persistent-hint
                  class="rounded-lg"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="index < 5"
                      small
                      close
                      @click:close="removeItem('modelos', item.id)"
                      color="info"
                      text-color="white"
                      class="ma-1"
                    >
                      <v-icon small left>mdi-shape</v-icon>
                      {{ item.nombre }}
                    </v-chip>
                    <span
                      v-if="index === 5"
                      class="grey--text text-caption ml-2"
                    >
                      (+{{ form.criterios.modelos.length - 5 }} más)
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Tab: Tamaños -->
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.criterios.tamanios"
                  :items="tamaniosItems"
                  item-text="tamanio"
                  item-value="id"
                  label="Seleccionar tamaños"
                  outlined
                  dense
                  multiple
                  chips
                  deletable-chips
                  :error="hasInputError('criterios.tamanios')"
                  :error-messages="getInputError('criterios.tamanios')"
                  prepend-inner-icon="mdi-ruler"
                  :loading="loadingTamanios"
                  hint="Todos los productos de estos tamaños entrarán en la oferta"
                  persistent-hint
                  class="rounded-lg"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="index < 5"
                      small
                      close
                      @click:close="removeItem('tamanios', item.id)"
                      color="warning"
                      text-color="white"
                      class="ma-1"
                    >
                      <v-icon small left>mdi-ruler</v-icon>
                      {{ item.tamanio }}
                    </v-chip>
                    <span
                      v-if="index === 5"
                      class="grey--text text-caption ml-2"
                    >
                      (+{{ form.criterios.tamanios.length - 5 }} más)
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>

        <!-- Resumen de criterios seleccionados -->
        <v-alert v-if="totalCriteriosSeleccionados > 0" type="success" dense text class="mt-4">
          <strong>{{ totalCriteriosSeleccionados }}</strong> criterio(s) seleccionado(s)
        </v-alert>
        <v-alert v-else type="warning" dense text class="mt-4">
          Debes seleccionar al menos un criterio
        </v-alert>
      </v-card-text>
    </v-card>

    <v-row>

      <!-- Depósito -->
      <v-col cols="12" md="6">
        <v-select
          v-model="form.deposito"
          :items="depositosItems"
          :label="$t('oferta.labels.deposito')"
          outlined
          dense
          :error="hasInputError('deposito')"
          :error-messages="getInputError('deposito')"
          prepend-inner-icon="mdi-warehouse"
          :loading="loadingDepositos"
          :rules="[v => !!v || 'Campo requerido']"
          disabled
          class="rounded-lg"
        />
      </v-col>

      <!-- Medios de Pago (OBLIGATORIO) -->
      <v-col cols="12" md="6">
        <v-select
          v-model="form.mediosPago"
          :items="mediosPagoOptions"
          :label="$t('oferta.labels.mediosPago') + ' *'"
          outlined
          dense
          multiple
          chips
          deletable-chips
          :error="hasInputError('mediosPago')"
          :error-messages="getInputError('mediosPago')"
          prepend-inner-icon="mdi-credit-card-multiple"
          :rules="[v => (v && v.length > 0) || 'Debes seleccionar al menos un medio de pago']"
          hint="La oferta solo aplicará a estos medios de pago"
          persistent-hint
          class="rounded-lg"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip
              v-if="index < 2"
              small
              color="info"
              text-color="white"
              class="ma-1"
            >
              <v-icon small left>{{ getMedioPagoIcon(item) }}</v-icon>
              {{ item }}
            </v-chip>
            <span
              v-if="index === 2"
              class="grey--text text-caption ml-2"
            >
              (+{{ form.mediosPago.length - 2 }} más)
            </span>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <!-- Días de la semana - Card especial -->
    <v-card outlined class="dias-semana-card my-4" elevation="2">
      <v-card-title class="py-3 bg-gradient">
        <v-icon color="white" class="mr-2">mdi-calendar-week</v-icon>
        <span class="white--text">{{ $t('oferta.labels.diasSemana') }}</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <v-checkbox
          v-model="todosLosDias"
          label="Todos los días de la semana"
          @change="onTodosLosDiasChange"
          color="primary"
          hide-details
          class="mb-3"
        >
          <template v-slot:label>
            <span class="font-weight-bold">Todos los días de la semana</span>
          </template>
        </v-checkbox>
        
        <v-divider class="my-3"></v-divider>
        
        <v-row class="ma-0">
          <v-col 
            v-for="dia in diasSemanaOptions" 
            :key="dia.value"
            cols="6"
            sm="4"
            md="3"
            class="pa-2"
          >
            <v-checkbox
              v-model="form.diasSemana"
              :value="dia.value"
              :label="dia.label"
              :disabled="todosLosDias"
              color="success"
              hide-details
              class="dia-checkbox"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small :color="isDiaSelected(dia.value) ? 'success' : 'grey'" class="mr-1">
                    {{ getDiaIcon(dia.value) }}
                  </v-icon>
                  <span :class="isDiaSelected(dia.value) ? 'font-weight-bold' : ''">
                    {{ dia.label }}
                  </span>
                </div>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Fechas -->
    <v-row>
      <v-col cols="12" md="6">
        <date-input
          v-model="form.fechaInicio"
          :label="$t('oferta.labels.fechaInicio')"
          prepend-icon="mdi-calendar-start"
          color="primary"
          outlined
          dense
          :error="hasInputError('fechaInicio')"
          :error-messages="getInputError('fechaInicio')"
          class="rounded-lg"
        />
      </v-col>

      <v-col cols="12" md="6">
        <date-input
          v-if="!sinFechaLimite"
          v-model="form.fechaFin"
          :label="$t('oferta.labels.fechaFin')"
          prepend-icon="mdi-calendar-end"
          color="primary"
          outlined
          dense
          :error="hasInputError('fechaFin')"
          :error-messages="getInputError('fechaFin')"
          :min="form.fechaInicio"
          class="rounded-lg"
        />
        
        <v-checkbox
          v-model="sinFechaLimite"
          :label="$t('oferta.labels.sinFechaLimite')"
          @change="onSinFechaLimiteChange"
          color="warning"
          class="mt-2"
        >
          <template v-slot:label>
            <div class="d-flex align-center">
              <v-icon small color="warning" class="mr-1">mdi-infinity</v-icon>
              <span>{{ $t('oferta.labels.sinFechaLimite') }}</span>
            </div>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { DateInput } from "@dracul/dayjs-frontend";
import ProductoProvider from "../../../../producto/providers/ProductoProvider";
import { formatProductoAutocompleteLabel } from "../../../../producto/utils/productoDisplayLabel";
import DepositoProvider from "../../../../deposito/providers/DepositoProvider";
import MarcaProvider from "../../../../marca/providers/MarcaProvider";
import ModeloProvider from "../../../../modelo/providers/ModeloProvider";
import TamanioProvider from "../../../../tamanio/providers/TamanioProvider";

export default {
  name: "OfertaFormFields",
  components: { DateInput },
  props: {
    value: {
      type: Object,
      required: true,
    },
    inputErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      tipoCriterioActivo: 0,
      loadingProductos: false,
      loadingDepositos: false,
      loadingMarcas: false,
      loadingModelos: false,
      loadingTamanios: false,
      searchProductos: "",
      productosItems: [],
      depositosItems: [],
      marcasItems: [],
      modelosItems: [],
      tamaniosItems: [],
      todosLosDias: false,
      sinFechaLimite: false,
      diasSemanaOptions: [
        { label: "Lunes", value: "lunes" },
        { label: "Martes", value: "martes" },
        { label: "Miércoles", value: "miercoles" },
        { label: "Jueves", value: "jueves" },
        { label: "Viernes", value: "viernes" },
        { label: "Sábado", value: "sabado" },
        { label: "Domingo", value: "domingo" },
      ],
      mediosPagoOptions: [
        "Efectivo",
        "Tarjeta Debito",
        "Tarjeta Credito",
        "Transferencia",
      ],
    };
  },
  computed: {
    form: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    totalCriteriosSeleccionados() {
      const productos = this.form.criterios?.productos?.length || 0;
      const marcas = this.form.criterios?.marcas?.length || 0;
      const modelos = this.form.criterios?.modelos?.length || 0;
      const tamanios = this.form.criterios?.tamanios?.length || 0;
      return productos + marcas + modelos + tamanios;
    },
  },
  watch: {
    'form.diasSemana': {
      handler(newVal) {
        if (!newVal || newVal.length === 7) {
          this.todosLosDias = true;
        } else {
          this.todosLosDias = false;
        }
      },
      immediate: true,
    },
    'form.fechaFin': {
      handler(newVal) {
        if (!newVal) {
          this.sinFechaLimite = true;
        } else {
          this.sinFechaLimite = false;
        }
      },
      immediate: true,
    },
  },
  created() {
    this.loadDepositos();
    this.loadProductos();
    this.loadMarcas();
    this.loadModelos();
    this.loadTamanios();
  },
  methods: {
    validate() {
      // Validación manual ya que no usamos v-form aquí
      return true;
    },
    hasInputError(field) {
      return this.inputErrors && this.inputErrors[field];
    },
    getInputError(field) {
      if (this.inputErrors && this.inputErrors[field]) {
        return Array.isArray(this.inputErrors[field])
          ? this.inputErrors[field]
          : [this.inputErrors[field]];
      }
      return [];
    },
    onTodosLosDiasChange() {
      if (this.todosLosDias) {
        this.form.diasSemana = this.diasSemanaOptions.map((d) => d.value);
      } else {
        this.form.diasSemana = [];
      }
    },
    onSinFechaLimiteChange() {
      if (this.sinFechaLimite) {
        this.form.fechaFin = null;
      }
    },
    isDiaSelected(dia) {
      return this.form.diasSemana && this.form.diasSemana.includes(dia);
    },
    getDiaIcon(dia) {
      const icons = {
        lunes: 'mdi-numeric-1-circle',
        martes: 'mdi-numeric-2-circle',
        miercoles: 'mdi-numeric-3-circle',
        jueves: 'mdi-numeric-4-circle',
        viernes: 'mdi-numeric-5-circle',
        sabado: 'mdi-numeric-6-circle',
        domingo: 'mdi-numeric-7-circle',
      };
      return icons[dia] || 'mdi-calendar';
    },
    getMedioPagoIcon(medio) {
      const icons = {
        'Efectivo': 'mdi-cash',
        'Tarjeta Debito': 'mdi-credit-card',
        'Tarjeta Credito': 'mdi-credit-card-multiple',
        'Transferencia': 'mdi-bank-transfer',
      };
      return icons[medio] || 'mdi-currency-usd';
    },
    getProductoLabel(item) {
      if (item == null) return "";
      if (typeof item === "string" || typeof item === "number") {
        const id = String(item);
        const row = this.productosItems.find(
          (p) => String(p.value) === id || String(p.id) === id
        );
        if (row) return row.text || formatProductoAutocompleteLabel(row);
        return id;
      }
      if (item.text) return item.text;
      return formatProductoAutocompleteLabel(item);
    },
    removeItem(tipo, itemOrId) {
      if (!this.form.criterios[tipo]) return;
      const id =
        itemOrId != null && typeof itemOrId === "object"
          ? String(itemOrId.value ?? itemOrId.id ?? "")
          : String(itemOrId);
      const arr = this.form.criterios[tipo];
      const idx = arr.findIndex((x) => String(x) === id);
      if (idx > -1) arr.splice(idx, 1);
    },
    async loadProductos() {
      this.loadingProductos = true;
      try {
        const response = await ProductoProvider.fetchProductos();
        if (response.data && response.data.productoFetch) {
          this.productosItems = response.data.productoFetch.map((p) => {
            const id = p.id != null ? String(p.id) : "";
            return {
              ...p,
              id,
              value: id,
              text: formatProductoAutocompleteLabel({ ...p, id }),
            };
          });
        }
      } catch (error) {
        console.error("Error loading productos:", error);
      } finally {
        this.loadingProductos = false;
      }
    },
    searchProductosHandler(search) {
      if (search && search.length > 2) {
        this.loadProductos();
      }
    },
    async loadDepositos() {
      this.loadingDepositos = true;
      try {
        const response = await DepositoProvider.fetchDepositos();
        if (response.data && response.data.depositoFetch) {
          this.depositosItems = response.data.depositoFetch.map((d) => ({
            value: d.id,
            text: d.nombre,
          }));
        }
      } catch (error) {
        console.error("Error loading depositos:", error);
      } finally {
        this.loadingDepositos = false;
      }
    },
    async loadMarcas() {
      this.loadingMarcas = true;
      try {
        const response = await MarcaProvider.fetchMarcas();
        if (response.data && response.data.marcaFetch) {
          this.marcasItems = response.data.marcaFetch;
        }
      } catch (error) {
        console.error("Error loading marcas:", error);
      } finally {
        this.loadingMarcas = false;
      }
    },
    async loadModelos() {
      this.loadingModelos = true;
      try {
        const response = await ModeloProvider.fetchModelos();
        if (response.data && response.data.modeloFetch) {
          this.modelosItems = response.data.modeloFetch;
        }
      } catch (error) {
        console.error("Error loading modelos:", error);
      } finally {
        this.loadingModelos = false;
      }
    },
    async loadTamanios() {
      this.loadingTamanios = true;
      try {
        const response = await TamanioProvider.fetchTamanios();
        if (response.data && response.data.tamanioFetch) {
          this.tamaniosItems = response.data.tamanioFetch;
        }
      } catch (error) {
        console.error("Error loading tamanios:", error);
      } finally {
        this.loadingTamanios = false;
      }
    },
  },
};
</script>

<style scoped>
.oferta-form-fields {
  width: 100%;
}

.criterios-card {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
}

.criterios-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  border-color: #2196f3;
}

.dias-semana-card {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
}

.dias-semana-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  border-color: #667eea;
}

.bg-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

.dia-checkbox >>> .v-label {
  font-size: 14px;
}

.rounded-lg >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
}

/* Mejoras visuales en chips */
.v-chip {
  font-weight: 500;
}

/* Animaciones sutiles */
.v-chip {
  transition: all 0.2s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Tabs personalizados */
.v-tabs >>> .v-tab {
  font-weight: 500;
}

.v-tabs >>> .v-tab--active {
  color: #2196f3;
}
</style>
